import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from './components/layout/Layout';

// Lazy load pages for better performance
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const SitesPage = React.lazy(() => import('./pages/sites/SitesPage'));
const ProductsPage = React.lazy(() => import('./pages/products/ProductsPage'));
const KeywordsPage = React.lazy(() => import('./pages/keywords/KeywordsPage'));
const ContentPage = React.lazy(() => import('./pages/content/ContentPage'));
const PromptsPage = React.lazy(() => import('./pages/prompts/PromptsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/analytics/AnalyticsPage'));
const RealtimePage = React.lazy(() => import('./pages/realtime/RealtimePage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const WorkflowsPage = React.lazy(() => import('./pages/workflows/WorkflowsPage'));
const WorkflowConfigsPage = React.lazy(() => import('./pages/workflows/WorkflowConfigsPage'));
const AliExpressTestPage = React.lazy(() => import('./pages/affiliate-test/AliExpressTestPage'));
const AffiliatePage = React.lazy(() => import('./pages/affiliate/AffiliatePage'));
const AISettingsPage = React.lazy(() => import('./pages/ai-settings/AISettingsPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <span className="spinner w-8 h-8" />
        <p className="mt-4 text-gray-500">加载中...</p>
      </div>
    </div>
  );
}

// Error boundary component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<{children: React.ReactNode}, ErrorBoundaryState> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Page Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">页面加载失败</h2>
            <p className="text-gray-600 mb-4">{this.state.error?.message || '未知错误'}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <React.Suspense fallback={<PageLoader />}>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="sites" element={<SitesPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="keywords" element={<KeywordsPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="prompts" element={<PromptsPage />} />
            <Route path="affiliate" element={<AffiliatePage />} />
            <Route path="ai-settings" element={<AISettingsPage />} />
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="workflow-configs" element={<WorkflowConfigsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="realtime" element={<RealtimePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="affiliate-test" element={<AliExpressTestPage />} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </React.Suspense>
  );
}

export default App;
